import { Inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NumberFormatterService } from '../services/number/number-formatter.service';

export interface NumberPipeParameters {
  format?: string;
  prependSign?: boolean;
  color?: boolean;
  emptyPlaceholder?: string;
  nullPlaceholder?: string;
  shorten?: boolean;
}

export class ShortenData {
  rangeLabel: string;
  value: number;
}

class FormatResult {
  formattedValue: string;
  precision?: number;
}

export abstract class BaseNumberPipe {

  private static NEGATIVE_VALUE_DEFAULT_CLASS: string = 'td-wb-red';
  private static POSITIVE_VALUE_DEFAULT_CLASS: string = 'td-wb-green';
  private static ZERO_VALUE_DEFAULT_CLASS: string = '';

  protected constructor(@Inject(TranslateService) protected translateService: TranslateService) {
  }

  protected static applyParameterDefaults(pipeParameters: NumberPipeParameters = {}, format?: string): NumberPipeParameters {
    const params: NumberPipeParameters = pipeParameters;
    params.format = params.format !== undefined ? params.format : format;
    return params;
  }

  protected static getSpanWithText(textValue: string, spanClass: string): string {
    return `<span class="${spanClass}">${textValue}</span>`;
  }

  private static prependPlusSign(numberValue: number | null | undefined, precision: number| undefined, formattedNumber: string): string {
    let valueWithAdjustedPrecision: number | null | undefined = numberValue;
    if (precision !== undefined) {
      valueWithAdjustedPrecision = parseFloat(numberValue?.toFixed(precision) );
    }
    return (valueWithAdjustedPrecision ?? 0) > 0 ? `+${formattedNumber}` : formattedNumber;
  }

  // select text color
  private static applyStyle(value: number | undefined, precision: number | undefined, formattedValue: string): string {
    let spanClass: string;

    if (precision !== undefined) {
      value = parseFloat(value?.toFixed(precision) ?? '');
    }
    if (value !== undefined && value < 0) {
      spanClass = BaseNumberPipe.NEGATIVE_VALUE_DEFAULT_CLASS;
    } else if (typeof value === 'number' && value > 0) {
      spanClass = BaseNumberPipe.POSITIVE_VALUE_DEFAULT_CLASS;
    } else {
      spanClass = BaseNumberPipe.ZERO_VALUE_DEFAULT_CLASS;
    }
    return BaseNumberPipe.getSpanWithText(formattedValue, spanClass);
  }

  protected renderValue(value: number | null | undefined, pipeParameters: NumberPipeParameters): string {
    let formattedValue: string;
    let formatData: FormatResult | undefined;
    let shortenData: ShortenData | undefined;
    let valueToFormat: number | null | undefined = value;

    if (valueToFormat === undefined) { // no value - applying placeholders
      if (pipeParameters.emptyPlaceholder !== undefined) {
        formattedValue = pipeParameters.emptyPlaceholder;
      } else {
        formattedValue = this.translateService.instant('tdWb.core.pipes.NA');
      }
    } else if (valueToFormat === null) {
      if (pipeParameters.nullPlaceholder !== undefined) {
        formattedValue = pipeParameters.nullPlaceholder;
      } else {
        formattedValue = this.translateService.instant('tdWb.core.pipes.NA');
      }
    } else {
      if (typeof valueToFormat !== 'number') { // fall back to toString for all non-numeric values
        return (valueToFormat as unknown).toString();
      }
      if (pipeParameters.shorten ?? false) {
        shortenData = this.getShortenData(valueToFormat);
        valueToFormat = shortenData.value;
      }
      formatData = this.formatValue(valueToFormat, pipeParameters.format);
      formattedValue = formatData.formattedValue;
      if (pipeParameters.prependSign ?? false) {
        formattedValue = BaseNumberPipe.prependPlusSign(valueToFormat, formatData.precision, formattedValue);
      }
      formattedValue = this.postFormat(formattedValue, pipeParameters, shortenData);
    }
    if ((pipeParameters.color ?? false) && !(valueToFormat === null || valueToFormat === undefined)) {
      formattedValue = BaseNumberPipe.applyStyle(valueToFormat, formatData?.precision, formattedValue);
    }
    return formattedValue;
  }

  protected postFormat(formattedValue: string, _pipeParameters: NumberPipeParameters, shortenData?: ShortenData): string {
    if (shortenData !== undefined && shortenData.rangeLabel !== undefined) {
      return `${formattedValue}${shortenData.rangeLabel}`;
    }
    return formattedValue;
  }

  private formatValue(value: number, format?: string | undefined): FormatResult {
    const formatType: string = format !== undefined ? format.charAt(0) : '';
    let formatPrecision: number | undefined;

    if (formatType !== undefined) {
      formatPrecision = NumberFormatterService.parsePrecision(value, format);
    }

    return {
      formattedValue: this.doFormat(value, format, formatPrecision),
      precision: formatPrecision
    };
  }

  private getShortenData(value: number): ShortenData {
    const absInput = Math.abs(value);
    const result = { rangeLabel: undefined, value } as unknown as ShortenData;
    let divisor = 1;
    // get range.
    if (absInput >= 999 * 1000 * 1000 * 1000) {
      divisor = 1000 * 1000 * 1000 * 1000;
      result.rangeLabel = 'T';
    } else if (absInput >= 999 * 1000 * 1000) {
      divisor = 1000 * 1000 * 1000;
      result.rangeLabel = this.translateService.instant('tdWb.core.pipes.number.RANGE_BILLION');
    } else if (absInput >= 999 * 1000) {
      divisor = 1000 * 1000;
      result.rangeLabel = 'M';
    } else if (absInput >= 999) {
      divisor = 1000;
      result.rangeLabel = 'k';
    }
    if (result.rangeLabel !== undefined) {
      result.value = Number((value / divisor));
    }
    return result;
  }

  protected abstract doFormat(val: number, format: string | undefined, precision: number | undefined): string;
}
